<template>
  <div class="chatbot-list">
    <!-- Utilisation du composant Notification -->
    <Notification 
      v-model:show="showNotification" 
      :message="notificationMessage" 
      :type="notificationType"
    />
    
    <!-- Header -->
    <header class="content-header">
      <h1>Chatbots</h1>
      <div class="header-subtitle">GÉRER VOS CHATBOTS</div>
      
      <div class="header-actions">
        <button class="add-chatbot-btn" @click="$router.push('/dashboard/chatbots/create')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>Nouveau Chatbot</span>
        </button>
      </div>
    </header>

    <!-- Search and Filter -->
    <div class="search-filter">
      <div class="search-box">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Rechercher un chatbot..."
        >
      </div>
      
      <div class="filter-options">
        <select v-model="statusFilter">
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
          <option value="maintenance">En maintenance</option>
        </select>
        
        <button class="sort-button" @click="toggleSortOrder">
          <svg v-if="sortOrder === 'desc'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 4h13M3 8h9M3 12h5M21 10V20M17 16l4 4 4-4"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 4h13M3 8h9M3 12h5M21 14V4M17 8l4-4 4 4"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Chatbots Grid -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <p>Chargement des chatbots...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4M12 16h.01"/>
      </svg>
      
      <p>{{ error }}</p>
      <button v-if="error.includes('reconnecter')" class="retry-btn" @click="router.push('/login')">
        Se reconnecter
      </button>
      <button v-else class="retry-btn" @click="fetchChatbots">
        Réessayer
      </button>
    </div>
    
    <div v-else>
      <div class="chatbots-grid">
        <div v-for="chatbot in displayedChatbots" :key="chatbot._id" class="chatbot-card" @click="openChatbot(chatbot, $event)">
          <div class="chatbot-header">
            <div class="chatbot-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="10" rx="2"/>
                <circle cx="12" cy="5" r="2"/>
                <path d="M12 7v4"/>
                <line x1="8" y1="16" x2="8.01" y2="16"/>
                <line x1="16" y1="16" x2="16.01" y2="16"/>
              </svg>
            </div>
            <div class="chatbot-info">
              <div class="chatbot-info-header">
                <h3>{{ chatbot.bot_name }}</h3>
                <div class="status-icon" :class="{ 'active': chatbot.status === 'active', 'inactive': chatbot.status !== 'active' }">
                  <svg v-if="chatbot.status === 'active'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20z"/>
                    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20z"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" fill="none"/>
                  </svg>
                </div>
              </div>
              <p>{{ truncateDescription(chatbot.description) }}</p>
              
            </div>
            <div class="chatbot-actions">
              <button class="menu-btn" @click="toggleMenu(chatbot._id)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="1"/>
                  <circle cx="19" cy="12" r="1"/>
                  <circle cx="5" cy="12" r="1"/>
                </svg>
              </button>
              <div v-if="activeMenu === chatbot._id" class="chatbot-menu">
                <button class="menu-item" @click="editChatbot(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  <span>Éditer paramètres</span>
                </button>
                <button class="menu-item" @click="confirmClone(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  <span>Cloner ce chatbot</span>
                </button>
                <button class="menu-item delete" @click="confirmDelete(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  <span>Supprimer</span>
                </button>
              </div>
            </div>
          </div>
          <div class="chatbot-stats">
            <div class="stat">
              <span class="stat-value">{{ formatNumber(chatbot.interactionCount) }}</span>
              <span class="stat-label">Conversations</span>
            </div>
            <div class="stat">
              <span class="stat-value">{{ formatNumber(chatbot.userCount) }}</span>
              <span class="stat-label">Utilisateurs</span>
            </div>
            <div class="stat">
              <span class="stat-value">
                <svg v-if="chatbot.status === 'active'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M15 9l-6 6M9 9l6 6"/>
                </svg>
                {{ chatbot.type }}
              </span>
              <span class="stat-label">Type</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Infinite Scroll Observer -->
      <div v-if="hasMoreChatbots" class="infinite-scroll-observer" ref="observerElement">
        <div class="loading-spinner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
      </div>
    </div>

    <!-- Add Chatbot Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Nouveau Chatbot</h3>
          <button class="close-btn" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nom du chatbot</label>
            <input v-model="newChatbot.name" type="text" placeholder="Entrez le nom du chatbot">
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="newChatbot.description" placeholder="Décrivez votre chatbot"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Annuler</button>
          <button class="save-btn" @click="addChatbot">Créer</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="close-btn" @click="showDeleteModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer ce chatbot ? Cette action est irréversible.</p>
          <div class="form-group">
            <label>Pour confirmer, tapez "SUPPRIMER" en majuscules</label>
            <input 
              v-model="deleteConfirmation" 
              type="text" 
              placeholder="SUPPRIMER"
              :class="{ 'error': deleteConfirmation && deleteConfirmation !== 'SUPPRIMER' }"
            >
            <span v-if="deleteConfirmation && deleteConfirmation !== 'SUPPRIMER'" class="error-message">
              Veuillez taper exactement "SUPPRIMER" en majuscules
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteModal = false">Annuler</button>
          <button 
            class="delete-btn" 
            @click="deleteChatbot" 
            :disabled="deleteConfirmation !== 'SUPPRIMER'"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Chatbot Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Modifier le chatbot</h3>
          <button class="close-btn" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nom du chatbot</label>
            <input v-model="editingChatbot.bot_name" type="text" placeholder="Entrez le nom du chatbot">
          </div>
          <div class="form-group">
            <label>ID de l'application</label>
            <input v-model="editingChatbot.app_id" type="text" placeholder="Entrez l'ID de l'application">
          </div>
          <div class="form-group">
            <label>URL de l'avatar</label>
            <input v-model="editingChatbot.avatar" type="text" placeholder="Entrez l'URL de l'avatar">
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="editingChatbot.description" placeholder="Décrivez votre chatbot"></textarea>
          </div>
          <div class="form-group">
            <label>Statut</label>
            <select v-model="editingChatbot.status">
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="maintenance">En maintenance</option>
            </select>
          </div>
          <div class="advanced-options">
            <router-link 
              :to="{ name: 'ChatbotSettings', params: { id: editingChatbot._id }}" 
              class="chatbot-link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span>Paramètres avancés</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </router-link>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Annuler</button>
          <button class="save-btn" @click="saveChatbot">Enregistrer</button>
        </div>
      </div>
    </div>

    <!-- Clone Chatbot Modal -->
    <div v-if="showCloneModal" class="modal-overlay" @click="showCloneModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Cloner le chatbot</h3>
          <button class="close-btn" @click="showCloneModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Vous allez cloner le chatbot "<strong>{{ chatbotToClone?.bot_name }}</strong>"</p>
          <div class="form-group">
            <label>Nom du nouveau chatbot</label>
            <input 
              v-model="cloneName" 
              type="text" 
              placeholder="Entrez le nom du chatbot cloné"
              :class="{ 'error': cloneName && cloneName.trim() === '' }"
            >
            <span v-if="cloneName && cloneName.trim() === ''" class="error-message">
              Le nom ne peut pas être vide
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showCloneModal = false">Annuler</button>
          <button 
            class="save-btn" 
            @click="cloneChatbot" 
            :disabled="!cloneName || cloneName.trim() === '' || isCloning"
          >
            <span v-if="!isCloning">Cloner</span>
            <span v-else>Clonage en cours...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !error && displayedChatbots.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      <h3>Aucun chatbot trouvé</h3>
      <p>Commencez par créer votre premier chatbot</p>
      <button class="create-btn" @click="$router.push('/dashboard/chatbots/create')"  >Créer un chatbot</button>
    </div>

    <!-- Notification -->
    
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import Notification from './Notification.vue';

export default {
  name: 'ChatbotList',
  components: {
    Notification
  },
  setup() {
    const router = useRouter();
    const chatbots = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const searchQuery = ref('');
    const statusFilter = ref('all');
    const sortOrder = ref('desc');
    const showAddModal = ref(false);
    const showDeleteModal = ref(false);
    const activeMenu = ref(null);
    const chatbotToDelete = ref(null);
    const deleteConfirmation = ref('');
    const displayLimit = ref(6);
    const newChatbot = ref({
      name: '',
      description: ''
    });
    const showNotification = ref(false);
    const showEditModal = ref(false);
    const editingChatbot = ref(null);
    const notificationMessage = ref('');
    const notificationType = ref('success');
    const showCloneModal = ref(false);
    const chatbotToClone = ref(null);
    const cloneName = ref('');
    const isCloning = ref(false);

    const showNotificationMessage = (message, type) => {
      notificationMessage.value = message;
      notificationType.value = type;
      showNotification.value = true;
    };

    const formatNumber = (number) => {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    const fetchChatbots = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        // Récupérer le token depuis localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          error.value = 'Données utilisateur non trouvées. Veuillez vous reconnecter.';
          loading.value = false;
          return;
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        if (!token) {
          error.value = 'Token d\'authentification non trouvé. Veuillez vous reconnecter.';
          loading.value = false;
          return;
        }
        
        // Faire la requête avec le token d'authentification
        const response = await axios.get(`${apiBaseUrl}/get/list/chatbots`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.chatbots && response.data.chatbots.length > 0) {
          chatbots.value = response.data.chatbots.map(chatbot => {
            if (!chatbot.update_date && !chatbot.updatedAt && !chatbot.updated_at) {
              return {
                ...chatbot,
                update_date: new Date().toISOString()
              };
            }
            return chatbot;
          });
          
          chatbots.value.sort((a, b) => {
            const dateA = new Date(a.update_date || 0);
            const dateB = new Date(b.update_date || 0);
            
            if (sortOrder.value === 'asc') {
              return dateA - dateB;
            } else {
              return dateB - dateA;
            }
          });
        } else {
          chatbots.value = response.data.chatbots || [];
        }
        
        displayLimit.value = 6;
        
        nextTick(() => {
          initInfiniteScroll();
        });
      } catch (err) {
        console.error('Erreur lors du chargement des chatbots:', err);
        
        // Gestion des erreurs spécifiques de l'API
        if (err.response) {
          const status = err.response.status;
          const errorData = err.response.data;
          
          switch (status) {
            case 401:
              error.value = 'Session expirée ou non autorisée. Veuillez vous reconnecter.';
              // Rediriger vers la page de connexion après un délai
              setTimeout(() => {
                localStorage.removeItem('user');
                router.push('/login');
              }, 3000);
              break;
            case 404:
              // Pour les erreurs 404, ne pas définir de message d'erreur
              // mais vider la liste des chatbots pour afficher l'état vide
              error.value = null;
              chatbots.value = []; 
              break;
            case 500:
              error.value = errorData.error || 'Erreur interne du serveur.';
              break;
            default:
              error.value = errorData.error || `Erreur lors du chargement des chatbots (${status}).`;
          }
        } else if (err.request) {
          // La requête a été faite mais aucune réponse n'a été reçue
          error.value = 'Impossible de communiquer avec le serveur. Veuillez vérifier votre connexion.';
        } else {
          // Une erreur s'est produite lors de la configuration de la requête
          error.value = err.message || 'Une erreur est survenue lors du chargement des chatbots.';
        }
      } finally {
        loading.value = false;
      }
    };

    const filteredChatbots = computed(() => {
      let filtered = [...chatbots.value];

      filtered = filtered.filter(chatbot => {
        const matchesSearch = chatbot.bot_name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                            chatbot.description.toLowerCase().includes(searchQuery.value.toLowerCase());
        const matchesStatus = statusFilter.value === 'all' || chatbot.status === statusFilter.value;
        return matchesSearch && matchesStatus;
      });

      filtered.sort((a, b) => {
        const timeA = new Date(a.update_date || 0).getTime();
        const timeB = new Date(b.update_date || 0).getTime();
        return sortOrder.value === 'desc' ? timeB - timeA : timeA - timeB;
      });

      return filtered;
    });

    const displayedChatbots = computed(() => {
      return filteredChatbots.value.slice(0, displayLimit.value);
    });

    const hasMoreChatbots = computed(() => {
      return filteredChatbots.value.length > displayLimit.value;
    });

    const loadMore = () => {
      if (filteredChatbots.value.length > displayLimit.value) {
        displayLimit.value += 6;
      }
    };

    const initInfiniteScroll = () => {
      if (observer) {
        observer.disconnect();
      }

      nextTick(() => {
        const element = document.querySelector('.infinite-scroll-observer');
        if (element) {
          observerElement.value = element;
          
          observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting && hasMoreChatbots.value) {
              loadMore();
            }
          }, {
            root: null,
            rootMargin: '100px',
            threshold: 0.1
          });

          observer.observe(element);
        }
      });
    };

    watch(filteredChatbots, (newFiltered, oldFiltered) => {
      if (newFiltered.length !== oldFiltered?.length) {
        displayLimit.value = 6;
        nextTick(() => {
          initInfiniteScroll();
        });
      }
    }, { deep: false, immediate: false });

    const toggleSortOrder = () => {
      sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
    };

    const toggleMenu = (chatbotId) => {
      activeMenu.value = activeMenu.value === chatbotId ? null : chatbotId;
    };

    const confirmDelete = (chatbot) => {
      chatbotToDelete.value = chatbot;
      showDeleteModal.value = true;
      activeMenu.value = null;
      deleteConfirmation.value = '';
    };

    const deleteChatbot = async () => {
      if (!chatbotToDelete.value) return;

      try {
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          showNotificationMessage('Données utilisateur non trouvées', 'error');
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          showNotificationMessage('Token non trouvé', 'error');
          return;
        }

        // Envoyer la requête de suppression
        await axios.delete(
          `${apiBaseUrl}/delete/strong/chatbot/${chatbotToDelete.value._id}`,
          {
            headers: {
              'Authorization': `Bearer ${userObj.data.token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Supprimer le chatbot de la liste locale
        chatbots.value = chatbots.value.filter(c => c._id !== chatbotToDelete.value._id);
        
        // Fermer la modal et réinitialiser
        showDeleteModal.value = false;
        chatbotToDelete.value = null;
        deleteConfirmation.value = '';
        
        // Afficher la notification de succès
        showNotificationMessage('Chatbot supprimé avec succès', 'success');
      } catch (err) {
        showNotificationMessage(err.response?.data?.error || 'Erreur lors de la suppression du chatbot', 'error');
      }
    };

    const addChatbot = () => {
      if (newChatbot.value.name && newChatbot.value.description) {
        const newId = Math.max(...chatbots.value.map(c => c.id)) + 1;
        chatbots.value.push({
          id: newId,
          ...newChatbot.value,
          conversationCount: 0,
          userCount: 0,
          uptime: 100
        });
        showAddModal.value = false;
        newChatbot.value = { name: '', description: '' };
      }
    };

    const editChatbot = (chatbot) => {
      editingChatbot.value = { ...chatbot };
      showEditModal.value = true;
      activeMenu.value = null;
    };

    const closeModal = () => {
      showEditModal.value = false;
      editingChatbot.value = null;
      activeMenu.value = null;
    };

    const saveChatbot = async () => {
      try {
        if (editingChatbot.value) {
          const userData = localStorage.getItem('user');
          if (!userData) {
            showNotification.value = true;
            notificationType.value = 'error';
            notificationMessage.value = 'Erreur: Données utilisateur non trouvées';
            return;
          }

          let token;
          try {
            const userObj = JSON.parse(userData);
            if (userObj.data && userObj.data.token) {
              token = userObj.data.token;
            } else {
              showNotification.value = true;
              notificationType.value = 'error';
              notificationMessage.value = 'Erreur: Structure de données utilisateur invalide';
              return;
            }
          } catch (parseError) {
            showNotification.value = true;
            notificationType.value = 'error';
            notificationMessage.value = 'Erreur: Format de données utilisateur invalide';
            return;
          }

          await axios.put(
            `${apiBaseUrl}/update/ease/chatbot/${editingChatbot.value._id}`,
            editingChatbot.value,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          showNotification.value = true;
          notificationType.value = 'success';
          notificationMessage.value = 'Chatbot modifié avec succès';
          await fetchChatbots();
          closeModal();
        }
      } catch (err) {
        console.error('Erreur lors de la modification du chatbot:', err);
        showNotification.value = true;
        notificationType.value = 'error';
        notificationMessage.value = err.response?.data?.error || 'Erreur lors de la modification du chatbot';
      }
    };

    const truncateDescription = (description) => {
      if (!description) return '';
      const maxLength = 100;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength) + '...';
    };

    // Référence à l'élément d'observation pour l'infinite scroll
    const observerElement = ref(null);
    let observer = null;

    const openChatbot = (chatbot, event) => {
      // Éviter la redirection si on clique sur le menu ou ses options
      if (event.target.closest('.menu-btn') || event.target.closest('.chatbot-menu')) {
        return;
      }
      router.push(`/dashboard/chatbots/${chatbot._id}/home`);
    };

    const confirmClone = (chatbot) => {
      chatbotToClone.value = chatbot;
      cloneName.value = `${chatbot.bot_name} (Clone)`;
      showCloneModal.value = true;
      activeMenu.value = null;
    };

    const cloneChatbot = async () => {
      if (!chatbotToClone.value || !cloneName.value.trim()) return;

      isCloning.value = true;

      try {
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          showNotificationMessage('Données utilisateur non trouvées', 'error');
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          showNotificationMessage('Token non trouvé', 'error');
          return;
        }

        // Envoyer la requête de clonage
        const response = await axios.post(
          `${apiBaseUrl}/strong/clonefinal/chatbot/${chatbotToClone.value._id}`,
          {
            namebotnew: cloneName.value.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${userObj.data.token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Afficher la notification de succès
          showNotificationMessage(response.data.message || 'Chatbot cloné avec succès', 'success');
          
          // Fermer la modal
          showCloneModal.value = false;
          chatbotToClone.value = null;
          cloneName.value = '';
          
          // Recharger la page après un court délai pour voir la notification
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          throw new Error(response.data.message || 'Erreur lors du clonage');
        }
      } catch (err) {
        console.error('Erreur lors du clonage du chatbot:', err);
        showNotificationMessage(
          err.response?.data?.message || err.message || 'Erreur lors du clonage du chatbot', 
          'error'
        );
      } finally {
        isCloning.value = false;
      }
    };

    onMounted(() => {
      fetchChatbots();
    });

    // Nettoyer l'observer lors de la destruction du composant
    onUnmounted(() => {
      if (observer) {
        observer.disconnect();
      }
    });

    return {
      chatbots,
      loading,
      error,
      searchQuery,
      statusFilter,
      sortOrder,
      showAddModal,
      showDeleteModal,
      activeMenu,
      chatbotToDelete,
      deleteConfirmation,
      newChatbot,
      displayedChatbots,
      hasMoreChatbots,
      loadMore,
      toggleMenu,
      confirmDelete,
      deleteChatbot,
      addChatbot,
      editChatbot,
      showNotification,
      showEditModal,
      editingChatbot,
      saveChatbot,
      notificationMessage,
      notificationType,
      formatNumber,
      truncateDescription,
      closeModal,
      observerElement,
      toggleSortOrder,
      showNotificationMessage,
      openChatbot,
      showCloneModal,
      chatbotToClone,
      cloneName,
      isCloning,
      confirmClone,
      cloneChatbot
    };
  }
};
</script>

<style scoped>
.chatbot-list {
  padding: 24px 16px;
}

/* Header */
.content-header {
  margin-bottom: 40px;
}

h1 {
  font-size: 42px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.header-subtitle {
  color: #6F767E;
  font-size: 15px;
  margin-top: 8px;
  font-weight: 500;
}

.header-actions {
  margin-top: 24px;
}

.add-chatbot-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #1ABB34;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-chatbot-btn:hover {
  background: #159B2B;
  transform: translateY(-1px);
}

/* Search and Filter */
.search-filter {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #FFFFFF;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
}

.search-box svg {
  color: #6F767E;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1A1D1F;
}

.search-box input::placeholder {
  color: #6F767E;
}

.filter-options {
  display: flex;
  gap: 12px;
}

.filter-options select {
  padding: 12px 16px;
  background: #FFFFFF;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  font-size: 14px;
  color: #1A1D1F;
  cursor: pointer;
  min-width: 140px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding-right: 40px;
}

.sort-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6F767E;
}

.sort-button:hover {
  color: #1ABB34;
}

.sort-button svg {
  transition: transform 0.2s ease;
}

.sort-button:hover svg {
  transform: scale(1.1);
}

/* Style pour l'icône de tri actuelle */
.filter-options select::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%231ABB34' stroke-width='2'%3E%3Cpath d='M12 5v14M5 12l7-7 7 7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
}

.filter-options select[value="asc"]::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%231ABB34' stroke-width='2'%3E%3Cpath d='M12 19V5M5 12l7 7 7-7'/%3E%3C/svg%3E");
}

/* Chatbots Grid */
.chatbots-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
}

.chatbot-card {
  background: #FFFFFF;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.chatbot-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #1ABB34;
}

.chatbot-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
}

.chatbot-icon {
  width: 40px;
  height: 40px;
  background: #E8F8EA;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1ABB34;
}

.chatbot-info {
  flex: 1;
}

.chatbot-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.status-icon {
  display: flex;
  align-items: center;
}

.status-icon svg {
  width: 10px;
  height: 10px;
}

.status-icon.active svg {
  color: #1ABB34;
}

.status-icon.inactive svg {
  color: #BE123C;
}

.status-icon.active svg path {
  fill: currentColor;
}

.status-icon.inactive svg path {
  fill: currentColor;
  opacity: 0.9;
}

.chatbot-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.chatbot-info p {
  font-size: 13px;
  color: #6F767E;
  margin: 0;
}

.chatbot-actions {
  position: relative;
  z-index: 2;
}

.menu-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.menu-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.chatbot-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 100;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
  background: none;
  border: none;
  color: #1A1D1F;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background: #E8ECEF;
}

.menu-item.delete {
  color: #FF3B30;
}

.menu-item.delete:hover {
  background: #FFD5D5;
}

.chatbot-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-top: 20px;
  border-top: 1px solid #E8ECEF;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.stat:hover {
  background: #F9FAFB;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  letter-spacing: 0.5px;
  padding: 0 4px;
}

.stat-label {
  font-size: 13px;
  color: #6F767E;
  font-weight: 500;
  text-align: center;
}

.stat:last-child .stat-value {
  font-size: 14px;
  padding: 4px 8px;
  background: #E8F8EA;
  color: #1ABB34;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0;
}

.stat:last-child:hover .stat-value {
  background: #1ABB34;
  color: white;
  box-shadow: 0 0 12px rgba(26, 187, 52, 0.3);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #E8ECEF;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1A1D1F;
  margin-bottom: 8px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1D1F;
  background: white;
}

.form-group textarea {
  height: 100px;
  resize: vertical;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #E8ECEF;
}

.cancel-btn,
.save-btn,
.delete-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: none;
  border: 1px solid #E8ECEF;
  color: #1A1D1F;
}

.cancel-btn:hover {
  background: #E8ECEF;
}

.save-btn {
  background: #1ABB34;
  border: none;
  color: white;
}

.save-btn:hover {
  background: #159B2B;
}

.save-btn:disabled {
  background: #A0A0A0;
  cursor: not-allowed;
  opacity: 0.6;
}

.save-btn:disabled:hover {
  background: #A0A0A0;
  transform: none;
}

.delete-btn {
  background: #FF3B30;
  border: none;
  color: white;
}

.delete-btn:hover {
  background: #FFD5D5;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 64px 0;
}

.empty-state svg {
  color: #6F767E;
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 16px;
  color: #6F767E;
  margin: 0 0 24px 0;
}

.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #1ABB34;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn:hover {
  background: #159B2B;
  transform: translateY(-1px);
}

@media (max-width: 1400px) {
  .chatbots-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .chatbot-list {
    padding: 16px;
  }

  .chatbots-grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }
}

.form-group input.error {
  border-color: #FF3B30;
}

.error-message {
  display: block;
  color: #FF3B30;
  font-size: 12px;
  margin-top: 4px;
}

.delete-btn:disabled {
  background: #FFA5A5;
  cursor: not-allowed;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #F9FAFB;
  border-radius: 12px;
  margin: 20px 0;
}

.loading-state {
  color: #6F767E;
}

.loading-state p {
  margin-top: 16px;
  font-size: 16px;
}

.error-state {
  color: #EF4444;
  border: 1px dashed #FECACA;
  background-color: #FEF2F2;
}

.error-state svg {
  margin-bottom: 16px;
  color: #EF4444;
}

.error-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #B91C1C;
}

.error-state p {
  font-size: 16px;
  margin-bottom: 24px;
  max-width: 600px;
  color: #991B1B;
}

.retry-btn {
  padding: 8px 20px;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #1F2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-btn:hover {
  background-color: #F3F4F6;
  transform: translateY(-1px);
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1ABB34;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.advanced-options {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #E8ECEF;
}

.chatbot-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1A1D1F;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.chatbot-link:hover {
  color: #6F767E;
}

.chatbot-link svg {
  transition: transform 0.2s ease;
}

.chatbot-link:hover svg:last-child {
  transform: rotate(90deg);
}

select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1D1F;
  background: white;
  cursor: pointer;
}

select:focus {
  border-color: #1ABB34;
  outline: none;
}

/* Infinite Scroll Observer */
.infinite-scroll-observer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  margin-top: 20px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1ABB34;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

</style> 